import { Badge, Button, Checkbox, Divider, IconButton, Paper, Stack, Typography } from '@mui/material'
import { AddRounded, ContentCopyRounded, DeleteOutlineRounded } from '@mui/icons-material'
import { QcStudyDefinitionCreateFormValues } from '../../schemas/StudyAndSeriesDefinitionValidationSchema'
import { Control, useFieldArray, useWatch } from 'react-hook-form'
import SeriesDefinitionForm from './SeriesDefinitionForm'
import { Fragment } from 'react/jsx-runtime'
import MergeModal from '../modals/MergeModal'
import { QcTimepointContainerSubmission } from '@common/config/api/client'
import { v4 as uuidv4 } from 'uuid'
import { useStudyMetadataOptions } from '@study_setup/hooks/useStudyMetadataOptions'
import { useParams } from 'react-router-dom'
import { Option } from '@common/components/DynamicTextAutocomplete/DynamicTextAutocomplete'
import { SPACING } from '@common/theme/spacing'

export default function SeriesDefinitionFormList({
  control,
  qcTimepointContainerSubmissions,
}: {
  control: Control<QcStudyDefinitionCreateFormValues>
  qcTimepointContainerSubmissions: QcTimepointContainerSubmission[]
}) {
  const { fields, append, remove, insert, update, replace } = useFieldArray({
    control,
    name: 'series_definitions',
  })

  const { studyId } = useParams()
  const { seriesLabelOptions, error, isLoading } = useStudyMetadataOptions(studyId!, ['series_labels'])

  const [series_definitions, exam_metadata_ids] = useWatch({
    control,
    name: ['series_definitions', 'exam_metadata_ids'],
  })

  // Track which series labels are used and how many times
  const seriesLabelUsage = series_definitions.reduce(
    (acc, def) => {
      if (def.series_label_id) {
        if (!acc[def.series_label_id]) {
          acc[def.series_label_id] = []
        }
        // Track the series_label_index values for each label ID
        acc[def.series_label_id].push(def.series_label_index)
      }
      return acc
    },
    {} as Record<string, (number | null)[]>,
  )

  const availableSourceSeriesDescriptionOptions = qcTimepointContainerSubmissions
    .map((submission) =>
      submission.exam_metadata
        .filter((metadata) => exam_metadata_ids.includes(metadata.exam_metadata_id!))
        .map((metadata) =>
          metadata.series.map((series) => {
            return {
              value: series.series_metadata_id,
              label: series.description || '',
            }
          }),
        )
        .flat(),
    )
    .flat()

  const selectedSeries = series_definitions.filter((item) => item.is_selected)
  const unusedSourceSeriesDescriptions = availableSourceSeriesDescriptionOptions.filter(
    (item) => !series_definitions.map((item) => item.series_metadata_id).includes(item.value!),
  )
  const allSelected = series_definitions.length > 0 && series_definitions.every((item) => item.is_selected)
  const noneSelected = series_definitions.every((item) => !item.is_selected)
  const numberOfSelectedSeries = series_definitions.filter((item) => item.is_selected).length

  // Get the first available index for a specific series label
  const getNextAvailableIndex = (seriesLabelId: string): number => {
    const usedIndices = seriesLabelUsage[seriesLabelId] || []
    // Find the lowest unused index starting from 1
    let index = 1
    while (usedIndices.includes(index)) {
      index++
    }
    return index
  }

  // Generate label display text with index suffix if needed
  const getLabelDisplayText = (label: string, index: number | null | undefined): string => {
    if (index == null) return label
    return `${label}_${index.toString().padStart(2, '0')}`
  }

  const bulkDelete = () => {
    const filtered = series_definitions.filter((item) => !item.is_selected)
    replace(filtered)
  }

  const bulkDuplicate = () => {
    const newSeries = []
    for (const item of series_definitions) {
      newSeries.push(item)
      if (item.is_selected) {
        newSeries.push({
          ...item,
          image_number: '',
          new_series_description: '',
          series_label_id: '',
          is_selected: false,
          series_label_index: null,
        })
      }
    }
    replace(newSeries)
  }

  const toggleAllSelected = (checked: boolean) => {
    series_definitions.forEach((item, index) => {
      update(index, { ...item, is_selected: checked })
    })
  }

  const handleMerge = (new_series_description: string, index?: number) => {
    // Get the series_label_index from an existing item with this series_label_id
    const existingIndex =
      series_definitions.find((item) => item.series_label_id === new_series_description)?.series_label_index || null

    if (index !== undefined) {
      const newSeries = [...series_definitions]
      const [item] = newSeries.splice(index, 1)

      // Use the same index as other series with this label or create a new one
      item.series_label_id = new_series_description
      item.series_label_index = existingIndex

      let lastOccurrenceIndex = -1
      for (let i = newSeries.length - 1; i >= 0; i--) {
        if (newSeries[i].series_label_id === new_series_description) {
          lastOccurrenceIndex = i
          break
        }
      }
      if (lastOccurrenceIndex >= 0) {
        newSeries.splice(lastOccurrenceIndex + 1, 0, item)
      } else {
        newSeries.push(item)
      }
      replace(newSeries)
    } else {
      const newSeries = series_definitions.filter((item) => !item.is_selected)
      const updatedItems = selectedSeries.map((item) => ({
        ...item,
        series_label_id: new_series_description,
        series_label_index: existingIndex,
        is_selected: false,
      }))

      for (const item of updatedItems) {
        let lastOccurrenceIndex = -1
        for (let i = newSeries.length - 1; i >= 0; i--) {
          if (newSeries[i].series_label_id === new_series_description) {
            lastOccurrenceIndex = i
            break
          }
        }
        if (lastOccurrenceIndex >= 0) {
          newSeries.splice(lastOccurrenceIndex + 1, 0, item)
        } else {
          newSeries.push(item)
        }
      }
      replace(newSeries)
    }
  }

  const bulkAdd = () => {
    unusedSourceSeriesDescriptions.forEach((item) => {
      append({
        series_label_id: '',
        submission_series_id: uuidv4(),
        series_metadata_id: item.value!,
        series_description: item.label,
        instance_number_ranges: [],
        is_selected: false,
        max_instance_number: 0,
        series_label_index: null,
        order: 1,
      })
    })
  }

  if (isLoading) {
    return <Stack>Loading...</Stack>
  }
  if (error) {
    return <Stack>Error: {error.message}</Stack>
  }

  // Process the series label options to include labels with indexes where needed
  const newSeriesDescriptions = seriesLabelOptions

  return (
    <Stack spacing={SPACING.minSpacing}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="start"
          spacing={SPACING.minSpacing}
          pl={SPACING.seriesDefinitionsFormSpacing}
        >
          <Checkbox
            sx={{
              width: 32,
              height: 32,
            }}
            checked={allSelected}
            indeterminate={!noneSelected && !allSelected}
            onChange={(e) => toggleAllSelected(e.target.checked)}
          />
          {noneSelected ? (
            <Stack direction="row" alignItems="center" justifyContent="start" spacing={SPACING.spacingLg}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Series
              </Typography>
              <Badge badgeContent={fields.length} color="secondary" />
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" justifyContent="start" spacing={SPACING.spacingLg}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {`${numberOfSelectedSeries} series selected`}
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="start" spacing={SPACING.minSpacing}>
                <Divider orientation="vertical" flexItem />
                <IconButton
                  sx={{
                    width: 32,
                    height: 32,
                  }}
                  onClick={bulkDuplicate}
                >
                  <ContentCopyRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                </IconButton>
                <IconButton
                  sx={{
                    width: 32,
                    height: 32,
                  }}
                >
                  <MergeModal
                    handleMerge={handleMerge}
                    control={control}
                    newSeriesDescriptions={newSeriesDescriptions}
                  />
                </IconButton>
                <IconButton
                  sx={{
                    width: 32,
                    height: 32,
                  }}
                  onClick={bulkDelete}
                >
                  <DeleteOutlineRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                </IconButton>
              </Stack>
            </Stack>
          )}
          {}
        </Stack>
        <Button
          variant="contained"
          sx={{
            color: 'text.primary',
            py: SPACING.spacingSm,
            px: SPACING.buttonSpacing,
          }}
          onClick={bulkAdd}
          color="secondary"
        >
          Add all source series
        </Button>
      </Stack>

      <Stack>
        <Paper
          sx={{
            paddingY: SPACING.seriesDefinitionsFormSpacing,
            borderRadius: SPACING.borderRadiusXxl,
            bgcolor: 'paper.main',
          }}
        >
          <Stack spacing={SPACING.minSpacing}>
            {fields.map((item, index) => {
              const previousNewSeriesDescriptionOption =
                index > 0 ? series_definitions[index - 1]?.series_label_id || '' : ''

              const currentNewSeriesDescriptionOption = series_definitions[index]?.series_label_id || ''
              const currentSeriesLabelIndex = series_definitions[index]?.series_label_index

              // Prepare options for each row, handling duplication rules
              const availableNewSeriesDescriptionOptions = newSeriesDescriptions
                .map((option) => {
                  const isDuplicatable = option.additional_fields?.allow_qc_to_duplicate
                  const isCurrentlySelected = currentNewSeriesDescriptionOption === option.value

                  // If this is already selected by this row, always show it
                  if (isCurrentlySelected) {
                    return {
                      ...option,
                      label: getLabelDisplayText(option.label, currentSeriesLabelIndex),
                    }
                  }

                  // If duplicatable, include it with the next index
                  if (isDuplicatable) {
                    const nextIndex = getNextAvailableIndex(option.value)
                    return {
                      ...option,
                      label: getLabelDisplayText(option.label, nextIndex),
                      additional_fields: {
                        ...option.additional_fields,
                        series_label_index: nextIndex,
                      },
                    }
                  }

                  // If not duplicatable and not used elsewhere, include it
                  if (!seriesLabelUsage[option.value] || seriesLabelUsage[option.value].length === 0) {
                    return option
                  }

                  // Otherwise, don't include it
                  return null
                })
                .filter(Boolean) as Option[]

              return (
                <Fragment key={item.id}>
                  {index !== 0 &&
                    (currentNewSeriesDescriptionOption !== previousNewSeriesDescriptionOption ||
                      currentNewSeriesDescriptionOption === '') && <Divider />}
                  <SeriesDefinitionForm
                    control={control}
                    index={index}
                    qcTimepointContainerSubmissions={qcTimepointContainerSubmissions}
                    defaultNewSeriesDescriptionValue={currentNewSeriesDescriptionOption}
                    defaultSourceSeriesDescriptionValue={series_definitions[index]?.series_metadata_id || ''}
                    availableNewSeriesDescriptionOptions={availableNewSeriesDescriptionOptions}
                    availableSourceSeriesDescriptionOptions={availableSourceSeriesDescriptionOptions as Option[]}
                    handleDelete={() => remove(index)}
                    handleDuplicate={() => {
                      insert(index + 1, {
                        ...series_definitions[index],
                        series_label_id: '',
                        is_selected: false,
                        series_label_index: null,
                      })
                    }}
                    handleMerge={handleMerge}
                    allSeriesDescriptions={newSeriesDescriptions}
                    disableMerge={
                      Object.keys(seriesLabelUsage).length <= 1 && Object.values(seriesLabelUsage)[0]?.length <= 1
                    }
                    getNextAvailableIndex={getNextAvailableIndex}
                  />
                </Fragment>
              )
            })}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="start"
              paddingX={SPACING.seriesDefinitionsFormSpacing}
            >
              <Button
                variant="text"
                sx={{
                  color: 'text.primary',
                  p: SPACING.spacingSm,
                }}
                onClick={() => {
                  append({
                    instance_number_ranges: [],
                    series_label_id: '',
                    submission_series_id: uuidv4(),
                    series_metadata_id: '',
                    series_description: '',
                    is_selected: false,
                    max_instance_number: 0,
                    series_label_index: null,
                    order: 1,
                  })
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="start" spacing={SPACING.minSpacing}>
                  <AddRounded />
                  <Typography variant="button"> Add series </Typography>
                </Stack>
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  )
}
